import { SubProductAction } from "features/product/types/actions.subproduct";
import { ModalName } from "features/product/types/modals";
import { Property, PropertyType } from "features/product/types/subproduct";
import { MultilingualString } from "features/shared/types/string";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { BiImage, BiLink } from "react-icons/bi";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import useCurrentTableState from "../hooks/useCurrentTableState";
import PromotionTag from "../modals/promotionTagUploadModal/promotionTag";
import { StyledIconContainer, StyledInput } from "./inputs.styled";

interface Props {
    propertyAccessor: string;
    subProductId: string;
    columnIdx: number;
}

const TableCellInput = ({
    propertyAccessor,
    subProductId,
    columnIdx,
}: Props) => {
    const [property, setProperty] = useState<Property>();

    const { readOnlyMode, locale } = useSelector((state) => state.subProducts);
    const dispatch = useDispatch();

    const { columns, subProducts } = useCurrentTableState();

    useEffect(() => {
        setProperty(subProducts[subProductId]?.properties[propertyAccessor]);
    }, [subProducts, subProductId, propertyAccessor]);

    const handleCellChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            // only update locally for responsiveness
            if (readOnlyMode || columns[columnIdx].isFixed) return;
            if (!PropertyType.Text) throw new Error("PropertyType is not Text");
            setProperty({
                accessor: propertyAccessor,
                type: PropertyType.Text,
                value: {
                    ...(property?.value as MultilingualString),
                    [locale]: e.target.value,
                },
            });
        },
        [
            columnIdx,
            columns,
            locale,
            property?.value,
            propertyAccessor,
            readOnlyMode,
        ]
    );

    const handleCellUpdate = useCallback(() => {
        // send the update to the columns
        if (readOnlyMode) return;
        dispatch({
            type: SubProductAction.SET_SUBPRODUCT_PROPERTY_VALUE,
            subProductId,
            accessor: propertyAccessor,
            propertyValue: property?.value,
        });
    }, [dispatch, property, propertyAccessor, readOnlyMode, subProductId]);

    const handleToggleModal = useCallback(
        (modalId: ModalName, extraParams?: Record<string, any>) => {
            dispatch({
                type: SubProductAction.OPEN_MODAL,
                modal: {
                    id: modalId,
                    params: {
                        subProductId,
                        propertyAccessor,
                        ...(extraParams ?? {}),
                    },
                },
            });
        },
        [dispatch, propertyAccessor, subProductId]
    );

    const renderedInput = useMemo(() => {
        if (!property) return null;
        switch (property.type) {
            case PropertyType.Text:
                return (
                    <StyledInput
                        value={property.value?.[locale] || ""}
                        onChange={handleCellChange}
                        onBlur={() => handleCellUpdate()}
                        placeholder="..."
                        disabled={readOnlyMode}
                    />
                );
            case PropertyType.FileDownload:
                return (
                    <StyledIconContainer
                        onClick={() =>
                            handleToggleModal(ModalName.FileUploadModal)
                        }
                        hasValue={property.value !== ""}
                    >
                        <BiLink />
                    </StyledIconContainer>
                );
            case PropertyType.Image:
                return (
                    <StyledIconContainer
                        onClick={() =>
                            handleToggleModal(ModalName.FileUploadModal, {
                                isImageFile: true,
                            })
                        }
                        hasValue={property.value !== ""}
                    >
                        <BiImage />
                    </StyledIconContainer>
                );
            case PropertyType.PromotionTags:
                return (
                    <div
                        onClick={() =>
                            !readOnlyMode &&
                            handleToggleModal(ModalName.PromotionTagUploadModal)
                        }
                    >
                        <PromotionTag promotionTagType={property.value} />
                    </div>
                );
        }
    }, [
        handleCellChange,
        handleCellUpdate,
        handleToggleModal,
        locale,
        property,
        readOnlyMode,
    ]);

    return renderedInput;
};

export default TableCellInput;
